/**
 * The checkout site's two pages, on an origin of their own so that RevenueCat Web Billing's SDK and Stripe never run in
 * the app's. Both are reached from the app's Plan page by a link, hold no Revisa credentials and never call Revisa's
 * API: what was bought reaches the server by RevenueCat's webhook.
 *
 *   /checkout?org=&tier=&period=&currency=&country=&lang=&return=   buys `tier` (an offering) monthly or yearly for
 *            [&code=][&promo=1]                                     the org (RevenueCat's app user id), then goes
 *                                                                   back to the app; `code`: a discount code
 *                                                                   applied from the start, `promo=1`: a field for
 *                                                                   the buyer to type one in
 *   /manage?org=&lang=&return=                                     the org's subscription page (RevenueCat's), if it
 *                                                                   has one
 *
 * `return` is a path in the app (REVISA_APP_URL): nowhere else is anyone sent back to. The checkout starts in the
 * currency the app priced (`currency`); without one, the one charged in the buyer's country (`country`, else this
 * site's Worker's guess from their address, `/geo`).
 */
import { ErrorCode, type PurchaseParams, Purchases, PurchasesError } from "@revenuecat/purchases-js";
import { CHECKOUT_LABELS, type Lang, langOf, type State, termsUrl } from "./strings.ts";

/** What the page shows: where it stands (busy: something's on its way), and a way back to the app. */
export interface View {
  lang: Lang;
  state: State;
  busy: boolean;
  back: string;
  /** `failed`: what the checkout said. */
  detail?: string;
}

export interface Env {
  /** The app's origin (REVISA_APP_URL). */
  appUrl: string;
  /** RevenueCat Web Billing's public API key. */
  apiKey: string;
  show: (view: View) => void;
  /** Leaves for `url` (the app, or the subscription's page). */
  go: (url: string) => void;
  /** The visitor's country (ISO 3166-1 alpha-2), as this site's Worker sees it; null when it can't tell. */
  country: () => Promise<string | null>;
}

const ORG = /^org_[0-9a-z]{1,32}$/u;
const TIER = /^[a-z0-9_]{1,64}$/u;
const CURRENCY = /^[A-Z]{3}$/u;
const COUNTRY = /^[A-Z]{2}$/u;
/** A discount code (RevenueCat's Web discounts: Product catalog → Web discounts; case-insensitive there). */
const CODE = /^[A-Za-z0-9_-]{1,64}$/u;
const TRAILING = /\/+$/u;

/** The euro area (with Bulgaria, from 2026) and the states that use the euro without being in it: the server's list
 *  (server/src/billing.rs `EURO`) and the landing site's. */
const EURO = new Set("AT BE BG HR CY EE FI FR DE GR IE IT LV LT LU MT NL PT SK SI ES MC SM VA AD ME XK".split(" "));

/** The currency the checkout charges in `country`: reais in Brazil, euros in the euro area, US dollars elsewhere. */
export function currencyOf(country: string): string {
  return country === "BR" ? "BRL" : EURO.has(country) ? "EUR" : "USD";
}

/** `href` with `lang` (the page in another language: the address keeps it, and the checkout starts again in it). */
export function withLang(href: string, lang: Lang): string {
  const url = new URL(href);
  url.searchParams.set("lang", lang);
  return url.href;
}

/** The visitor's country, from this site's Worker (`/geo`, web/src/worker.ts); null when it can't tell. */
export async function visitorCountry(): Promise<string | null> {
  try {
    const body = (await (await fetch("/geo", { cache: "no-store" })).json()) as { country?: unknown };
    return typeof body.country === "string" && COUNTRY.test(body.country) ? body.country : null;
  } catch {
    return null;
  }
}

/** The app's address for `path`, if it is a path in the app; anything else (another site, `//host`) is refused. */
export function backTo(appUrl: string, path: string | null): string | null {
  if (!path?.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return null;
  }
  const app = new URL(appUrl);
  const url = new URL(path, app);
  return url.origin === app.origin ? url.href : null;
}

/** The checkout's currency: the link's, else the buyer's country's (the link's, else the Worker's); none: RevenueCat's
 *  default. */
async function currencyFor(q: URLSearchParams, env: Env): Promise<string | null> {
  const given = q.get("currency");
  if (given) {
    return given;
  }
  const country = q.get("country")?.toUpperCase() || (await env.country());
  return country && COUNTRY.test(country) ? currencyOf(country) : null;
}

/**
 * The checkout in Revisa's look (web/src/app.css), as far as purchases-js 1.67.1 lets it be set. `BrandingAppearance`
 * has these keys, all set here or said why not:
 *
 *   color_buttons_primary, _text   Prussian blue buttons, sheet-coloured words on them
 *   color_accent                   Prussian blue: links, icons, and the focused input's border (Stripe's fields too)
 *   color_error                    vermilion: errors, and Stripe's fields' `colorDanger`
 *   color_page_bg                  paper behind it all
 *   color_product_info_bg          paper: the left column (what's bought, the total)
 *   color_form_bg                  sheet: the form's column, and the background of every input (Stripe's included)
 *   shapes                         "rounded" (the same as "default"): inputs 4px, buttons 8px; the app's are 6px
 *                                  (rounded-md). "rectangle" is 0, "pill" 12px inputs and fully round buttons.
 *   show_product_description       left to the dashboard (products' descriptions are the dashboard's)
 *   font                           not set: 1.67.1 only reports it to RevenueCat's analytics. A checkout's custom font
 *                                  is the dashboard's (`brand_font_config`, served from RevenueCat's CDN, which the CSP
 *                                  allows) and reaches only RevenueCat's own text, never Stripe's fields.
 *
 * What can't be matched: Stripe's fields (email, card, country: Stripe Elements, in Stripe's frames) are styled by the
 * SDK alone, from the keys above: their text is black, their borders translucent black (rgba(0,0,0,.3), white ones on
 * a dark `color_form_bg`), their font the system's (the SDK hard-codes a system font stack and passes Stripe no
 * `fonts`), their size 14px. RevenueCat's own text (headings, prices, the footer, its discount-code field) is in this
 * page's DOM, so checkout.css gives it the app's ink and DM Sans.
 */
const BRANDING = {
  color_buttons_primary: "#2e5a94",
  color_buttons_primary_text: "#fffdf8",
  color_accent: "#2e5a94",
  color_error: "#d0482a",
  color_product_info_bg: "#f5f1e8",
  color_form_bg: "#fffdf8",
  color_page_bg: "#f5f1e8",
  shapes: "rounded",
} as const;

/** Runs the page at `page`: checks its link, then checks out or opens the subscription's page. */
export async function run(page: URL, env: Env): Promise<void> {
  const q = page.searchParams;
  const lang = langOf(q.get("lang"));
  const org = q.get("org") ?? "";
  const route = page.pathname.replace(TRAILING, "");
  const back = backTo(env.appUrl, q.get("return"));
  const show = (state: State, busy = false, detail?: string) =>
    env.show({ lang, state, busy, back: back ?? env.appUrl, ...(detail === undefined ? {} : { detail }) });
  if (!(back && ORG.test(org) && (route === "/checkout" || route === "/manage"))) {
    show("badLink");
    return;
  }
  const sdk = Purchases.configure({ apiKey: env.apiKey, appUserId: org });
  try {
    if (route === "/manage") {
      show("manage", true);
      const url = (await sdk.getCustomerInfo()).managementURL;
      if (url?.startsWith("https://")) {
        env.go(url);
      } else {
        show("noSubscription");
      }
      return;
    }
    const tier = q.get("tier") ?? "";
    const period = q.get("period");
    const given = q.get("currency") ?? "";
    const code = q.get("code") ?? "";
    if (
      !(
        TIER.test(tier) &&
        (period === "monthly" || period === "yearly") &&
        (given === "" || CURRENCY.test(given)) &&
        (code === "" || CODE.test(code))
      )
    ) {
      show("badLink");
      return;
    }
    show("checkout", true);
    const currency = await currencyFor(q, env);
    const offering = (await sdk.getOfferings(currency ? { currency } : {})).all[tier];
    const pkg = period === "yearly" ? offering?.annual : offering?.monthly;
    if (!pkg) {
      show("notSold");
      return;
    }
    show("paying", true);
    const params: PurchaseParams & { labelsOverride: typeof CHECKOUT_LABELS } = {
      rcPackage: pkg,
      // The checkout in the page's language (RevenueCat's own words; the product's name is the dashboard's).
      selectedLocale: lang === "pt-BR" ? "pt_BR" : lang,
      defaultLocale: "en",
      // Some of its words as Brazilians (and Italians) say them. The SDK takes them (its checkout's
      // `customTranslations`), though its public types leave the option out: checked by the tests.
      labelsOverride: CHECKOUT_LABELS,
      termsAndConditionsUrl: termsUrl(lang),
      // Revisa's look (the logo is the dashboard's: Web Billing → appearance).
      brandingAppearanceOverride: BRANDING,
      // Discounts (RevenueCat's Web discounts, percentage only; experimental in the SDK): the link's code, applied
      // from the start (the buyer can remove it), and with `promo=1` a field to type one in.
      ...(code === "" ? {} : { discountCode: code }),
      showDiscountCodeField: q.get("promo") === "1",
    };
    await sdk.purchase(params);
    show("paid", true);
    const done = new URL(back);
    done.searchParams.set("confirming", tier);
    env.go(done.href);
  } catch (e) {
    if (e instanceof PurchasesError && e.errorCode === ErrorCode.UserCancelledError) {
      show("cancelled");
    } else {
      show("failed", false, e instanceof Error ? e.message : String(e));
    }
  }
}
